
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import VFormBuilder from '@/components/shared/form/VFormBuilder.vue';
import { InputType } from '@/enums/InputType';
import { MerchantChannel } from '@/enums/SmoothrPay';
import { BusinessLineInput, CreateMerchantConfigurationRequest, MerchantAccount } from '@/interfaces/models/SmoothrPay';
import Countries from '@/mixins/Countries';

/**
 * Creates a merchant configuration in `draft`. Everything Adyen needs up front:
 * the legal entity, at least one business line, the sales channels, one balance
 * account description and the Smoothr user who owns it.
 *
 * The service validates the same rules — a business line selling online must
 * carry a web address — so the dialog refuses to submit without it rather than
 * bouncing off a 422.
 */
@Component({ components: { VFormBuilder } })
export default class MerchantConfigurationDialog extends mixins(Countries) {
  @Prop({ type: Boolean, default: false }) public visible!: boolean;
  @Prop({ type: Boolean, default: false }) public loading!: boolean;
  @Prop({ type: Array, default: () => [] }) public merchantAccounts!: MerchantAccount[];

  public $refs!: { form: InstanceType<typeof VFormBuilder> & { form: any; validate: () => Promise<boolean> } };

  public businessLines: BusinessLineInput[] = [this.emptyLine()];
  public error: string = '';

  get initialValues() {
    return { country: 'de', channels: [MerchantChannel.ECOM], balanceAccount: 'Main' };
  }

  get channelItems() {
    return [
      { text: this.$t('smoothrPay.channels.ecom'), value: MerchantChannel.ECOM },
      { text: this.$t('smoothrPay.channels.pos'), value: MerchantChannel.POS },
    ];
  }

  get accountItems() {
    return this.merchantAccounts.map((account: MerchantAccount) => ({
      text: account.isDefault ? `${account.id} (${this.$t('smoothrPay.fields.defaultAccount')})` : account.id,
      value: account.id,
    }));
  }

  get items() {
    return [
      {
        name: 'merchantAccount',
        type: InputType.Select,
        label: 'smoothrPay.fields.merchantAccount',
        items: this.accountItems,
        visible: this.merchantAccounts.length > 1,
      },
      { name: 'name', type: InputType.Text, label: 'smoothrPay.fields.legalName', rules: 'required' },
      { name: 'description', type: InputType.Text, label: 'smoothrPay.fields.description', rules: 'required' },
      {
        name: 'country',
        type: InputType.Select,
        label: 'smoothrPay.fields.country',
        items: this.countries,
        rules: 'required',
      },
      {
        name: 'channels',
        type: InputType.Select,
        label: 'smoothrPay.fields.channels',
        items: this.channelItems,
        multiple: true,
        rules: 'required',
      },
      {
        name: 'balanceAccount',
        type: InputType.Text,
        label: 'smoothrPay.fields.balanceAccount',
        rules: 'required',
      },
      { name: 'ownerId', type: InputType.Text, label: 'smoothrPay.fields.ownerId', rules: 'required' },
      { name: 'ownerName', type: InputType.Text, label: 'smoothrPay.fields.ownerName', rules: 'required' },
      { name: 'ownerEmail', type: InputType.Text, label: 'smoothrPay.fields.ownerEmail', rules: 'required|email' },
    ];
  }

  @Watch('visible')
  public onVisibleChange(visible: boolean) {
    if (visible) {
      this.businessLines = [this.emptyLine()];
      this.error = '';
    }
  }

  public emptyLine(): BusinessLineInput {
    return { industryCode: '', salesChannels: [MerchantChannel.ECOM], webAddress: '' };
  }

  public addLine() {
    this.businessLines.push(this.emptyLine());
  }

  public removeLine(index: number) {
    this.businessLines.splice(index, 1);
  }

  public async save() {
    this.error = '';
    const valid = await this.$refs.form.validate();
    if (!valid) {
      return;
    }
    const invalid = this.businessLines.find((line: BusinessLineInput) => !this.isLineValid(line));
    if (invalid) {
      this.error = this.$t('smoothrPay.create.invalidBusinessLine') as string;
      return;
    }

    const form: any = this.$refs.form.form;
    const payload: CreateMerchantConfigurationRequest = {
      legalEntity: { name: form.name, description: form.description, country: form.country },
      businessLines: this.businessLines.map((line: BusinessLineInput) => this.cleanLine(line)),
      channels: form.channels,
      balanceAccounts: [{ description: form.balanceAccount }],
      owner: { id: form.ownerId, name: form.ownerName, email: form.ownerEmail },
    };
    if (form.merchantAccount) {
      payload.merchantAccount = form.merchantAccount;
    }
    this.$emit('save', payload);
  }

  /** An online channel needs a web address; Adyen rejects the capability without one. */
  private isLineValid(line: BusinessLineInput): boolean {
    if (!line.industryCode || !line.salesChannels || line.salesChannels.length === 0) {
      return false;
    }
    return !line.salesChannels.includes(MerchantChannel.ECOM) || !!line.webAddress;
  }

  /** An empty web address must be absent, not '' — the service validates the URI. */
  private cleanLine(line: BusinessLineInput): BusinessLineInput {
    const cleaned: BusinessLineInput = { industryCode: line.industryCode, salesChannels: line.salesChannels };
    if (line.webAddress) {
      cleaned.webAddress = line.webAddress;
    }
    return cleaned;
  }
}
